import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "@pd/pd-ui-kit";
import { Col, Flex, Row } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import DeleteIcon from "~/components/Icons/svg/delete";
import Preloader from "~/components/Preloader";
import useRequestHandler from "~/shared/hooks/useRequestHandler";
import Wrapper from "~/shared/ui/Wrapper";
import { CourseLevel } from "~/entity/Courses/model/shema";
import { useEmployee } from "~/entity/Employee";
import {
  EmployeeEducationProgramCourses,
  EmployeeEducationProgramWithCourses,
  EmployeeEducationSetterParams,
} from "~/entity/Employee/model/schema";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { collectDataToServer } from "~/pages/Editor/EditorCatalogEducationalPrograms/EducationalProgramsManagement/model/adapter";
import { dateFormat } from "~/shared/lib/DateFormat/dateFormat";
import { Card } from "~/shared/ui/Card";
import { Title } from "~/shared/ui/Text/Title";
import { EducationalProgramsManagementControl } from "~/widgets/ControlComponent/EducationalProgramsManagementControl/EducationalProgramsManagementControl";
import { EducationalProgramsManagementFilter } from "~/widgets/Filters/EducationalProgramsManagementFilter";
import { AddEducationalProgramCourse } from "~/widgets/Modals/AddEducationalProgramCourse";
import { TAddCourseForm } from "~/widgets/Modals/AddEducationalProgramCourse/model/config";

import s from "./EducationalProgramsManagement.less";

export const EducationalProgramsManagement = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  // Открытие/закрытие модалок и запись фильтра — через replace,
  // чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { openChildModal } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const { positions: positionsRaw } = useGetLocationData() as Record<
    string,
    unknown
  >;
  const history = useHistory();
  const isView = history.location.pathname.includes("/editor/cep/view/ptp");
  const goBack = () =>
    isView
      ? history.replace("/editor/cep/view/ptp")
      : history.replace("/editor/cep/ptp/");

  // Фильтр должностей хранится в URL строкой через запятую (Filter.Root),
  // для запросов разворачиваем в массив id
  const positionIds = useMemo(
    () =>
      String(positionsRaw ?? "")
        .split(",")
        .filter(Boolean)
        .map(Number),
    [positionsRaw],
  );

  const { getEducationProgramsById, updateEducationProgram } = useEmployee();

  const [idToDelete, setIdToDelete] = useState<number>(null);
  const [loading, setLoading] = useState(true);
  const [levelList, setLevelList] = useState<
    Omit<CourseLevel, "status" | "sequenceNumber">[]
  >([]);
  const [educationProgramCourses, setEducationProgramCourses] =
    useState<Map<number, EmployeeEducationProgramCourses[]>>(null);

  const fetchEdProgram = () => {
    setLoading(true);
    getEducationProgramsById(id, {
      actualOn: dateFormat(new Date(), { type: "date-special" }),
    })
      .then(({ data }) => {
        const processedData = processEducationProgramData(data);
        setDataReplace(
          "positions",
          processedData.positionIds.join(","),
          "openChildModal",
          "",
        );
        setLevelList(processedData.levelList);
        setEducationProgramCourses(processedData.coursesMap);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const processEducationProgramData = (data: {
    program: EmployeeEducationProgramWithCourses;
    levels: CourseLevel[];
  }) => {
    const coursesMap = new Map();
    data.program.courses.forEach((course) => {
      const levelId = course.course.levelId;
      coursesMap.set(levelId, [...(coursesMap.get(levelId) ?? []), course]);
    });

    const positionIds = data.program.positions.map((el) => el.instanceId);

    const levelList = data.levels
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .map(({ instanceId, name }) => ({ instanceId, name }));

    return {
      coursesMap,
      positionIds,
      levelList,
    };
  };

  useEffect(() => {
    fetchEdProgram();
  }, []);

  const onAdd = () => setDataReplace("openChildModal", "addCourse");

  const closeChildModal = () => setDataReplace("openChildModal", "");

  const { requestWithNotifHandler } = useRequestHandler();

  const [loadingPutEdProgram, setLoadingPutEdProgram] = useState(false);

  const onSet = (params: EmployeeEducationSetterParams) => {
    setLoadingPutEdProgram(true);
    requestWithNotifHandler({
      request: () => updateEducationProgram(params),
      callback: () => {
        setLoadingPutEdProgram(false);
        fetchEdProgram();
      },
    });
  };

  const onAddNewCourse = ({ course }: TAddCourseForm) => {
    const newLevelId = levelList[0]?.instanceId;
    const params = collectDataToServer({
      positionIds,
      levelList,
      educationProgramCourses,
      instanceId: Number(id),
      newLevelId,
      newCourseId: Number(course),
    });
    onSet(params);
  };

  const onDeleteAgreeRequired = (idToDelete: number) => () => {
    setIdToDelete(idToDelete);
    setDataReplace("openChildModal", "deleteAgree");
  };

  const onDelete = (excludeCourseId: number) => () => {
    const params = collectDataToServer({
      positionIds,
      levelList,
      educationProgramCourses,
      excludeCourseId,
      instanceId: Number(id),
    });
    onSet(params);
  };

  const getSortByCode = (array: any[], path: string) =>
    array.sort((a, b) => Number(get(a, path, 0)) - Number(get(b, path, 0)));

  const getCoursesFromLevel = (
    levelId: number,
  ): EmployeeEducationProgramCourses[] =>
    getSortByCode(educationProgramCourses.get(levelId) ?? [], "course.codeIS");

  if (loading) return <Preloader />;

  return (
    <>
      <Wrapper.Content
        title={
          isView
            ? t("patp.viewing_training_program")
            : t("patp.edit_training_program")
        }
        txtBtn={t("ui.back")}
        filterComponent={<EducationalProgramsManagementFilter />}
        controlComponent={
          !isView && <EducationalProgramsManagementControl onAdd={onAdd} />
        }
        goBackHandler={goBack}
      >
        <Wrapper.InnerContent className={s.width}>
          <Row gutter={16}>
            {levelList.map((level) => {
              return (
                <Col span={8} key={level.instanceId}>
                  <Title
                    title={level.name}
                    size={"md"}
                    className={s.titleMargin}
                  />
                  <Flex gap={10} vertical>
                    {!!educationProgramCourses &&
                      (getCoursesFromLevel(level.instanceId) ?? []).map(
                        (courseCollectionItem) => {
                          return (
                            <Card.Collapsed
                              key={courseCollectionItem.course.instanceId}
                              cardTitle={"Основной курс"}
                              cardContent={
                                <CardContent
                                  isView={isView}
                                  codeIS={courseCollectionItem.course.codeIS}
                                  name={courseCollectionItem.course.name}
                                  onDelete={onDeleteAgreeRequired(
                                    courseCollectionItem.course.instanceId,
                                  )}
                                />
                              }
                              disabledCollapse={
                                !courseCollectionItem.alternativeCourses.length
                              }
                              collapsedCardTitle={"Альтернативные курсы"}
                              collapsedCardContent={
                                <Flex gap={16} vertical>
                                  {getSortByCode(
                                    courseCollectionItem.alternativeCourses,
                                    "codeIS",
                                  ).map((replacementCourse) => (
                                    <CardContent
                                      key={replacementCourse.codeIS}
                                      isView={isView}
                                      codeIS={replacementCourse.codeIS}
                                      name={replacementCourse.name}
                                    />
                                  ))}
                                </Flex>
                              }
                            />
                          );
                        },
                      )}
                  </Flex>
                </Col>
              );
            })}
          </Row>
        </Wrapper.InnerContent>
      </Wrapper.Content>
      <AddEducationalProgramCourse
        onSubmit={onAddNewCourse}
        isLoading={loadingPutEdProgram}
      />
      <Modal
        open={openChildModal === "deleteAgree" && idToDelete !== null}
        title={t("ui.confirm_action")}
        onCancel={closeChildModal}
        closable
        centered
        destroyOnHidden
        cancelButton={{
          children: t("ui.cancel"),
          disabled: loadingPutEdProgram,
        }}
        okButton={{
          children: t("ui.confirm"),
          onClick: onDelete(idToDelete),
          disabled: loadingPutEdProgram,
        }}
      >
        {t("patp.delete_course_confirm")}
      </Modal>
    </>
  );
};

const CardContent = ({
  isView,
  codeIS,
  name,
  onDelete,
}: {
  isView: boolean;
  codeIS: string;
  name: string;
  onDelete?: () => void;
}) => {
  return (
    <Flex gap={16} vertical>
      <Flex gap={4} vertical>
        <p className={s.code}>{codeIS}</p>
        <p className={s.text}>{name}</p>
      </Flex>
      {onDelete && !isView && (
        <Flex gap={6}>
          <Button
            variant="text"
            size={"middle"}
            icon={<DeleteIcon width={14} height={15} fill={"#495A64"} />}
            onClick={onDelete}
          />
        </Flex>
      )}
    </Flex>
  );
};
